<template>
  <div :class="containerClass">
    <page-header :title="title" :breadcrumb="breadcrumb" :container-class="null" class="mb-32pt" />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <!-- Test Provider -->
      <b-form-group label="Exam (Test Provider)" label-for="test_provider" label-class="form-label">
        <v-select
          id="test_provider"
          class="form-control v-select-custom"
          label="name"
          v-model="test_provider"
          :reduce="(item) => item.id"
          placeholder="Select Exam"
          :options="allProviders"
          :loading="areProvidersLoading"
          :disabled="!!$route.params.id"
          :filterable="!providersHaveMore"
          @search="onSearchProviders"
        >
          <template v-if="providersHaveMore" #no-options="{ searching }">
            {{ searching ? 'No exams found.' : 'Type to search exams…' }}
          </template>
        </v-select>
        <small v-if="!$route.params.id" class="form-text text-muted">
          Only exams available for your school's state are listed.
        </small>
        <b-alert v-if="duplicateResultId" show variant="warning" class="mt-2 mb-0">
          You already have a result for this exam.
          <b-link :to="{ name: 'student-edit-exam-result', params: { id: duplicateResultId } }">
            Edit the existing result
          </b-link>
          instead.
        </b-alert>
      </b-form-group>

      <template v-if="selectedProvider">
        <!-- Theory -->
        <div v-if="selectedProvider.theory_tested" class="row">
          <b-form-group label="Theory Result" label-for="theory_result" label-class="form-label" class="col-md-6">
            <v-select
              id="theory_result"
              class="form-control v-select-custom"
              label="text"
              v-model="theory_result"
              :reduce="(item) => item.value"
              placeholder="Not taken yet"
              :options="resultOptions"
            >
            </v-select>
          </b-form-group>
          <b-form-group label="Theory Date" label-for="theory_date" label-class="form-label" class="col-md-6">
            <date-picker
              id="theory_date"
              v-model="theory_date"
              type="date"
              format="MM/DD/YYYY"
              value-type="YYYY-MM-DD"
              style="width: 100%"
              lang="en"
              placeholder="MM/DD/YYYY"
            >
            </date-picker>
          </b-form-group>
        </div>

        <!-- Skill -->
        <div v-if="selectedProvider.skill_tested" class="row">
          <b-form-group label="Skill Result" label-for="skill_result" label-class="form-label" class="col-md-6">
            <v-select
              id="skill_result"
              class="form-control v-select-custom"
              label="text"
              v-model="skill_result"
              :reduce="(item) => item.value"
              placeholder="Not taken yet"
              :options="resultOptions"
            >
            </v-select>
          </b-form-group>
          <b-form-group label="Skill Date" label-for="skill_date" label-class="form-label" class="col-md-6">
            <date-picker
              id="skill_date"
              v-model="skill_date"
              type="date"
              format="MM/DD/YYYY"
              value-type="YYYY-MM-DD"
              style="width: 100%"
              lang="en"
              placeholder="MM/DD/YYYY"
            >
            </date-picker>
          </b-form-group>
        </div>

        <!-- Clinical competency -->
        <b-form-group
          v-if="selectedProvider.clinical_competency_required"
          label="Clinical Competency Met?"
          label-for="clinical"
          label-class="form-label"
        >
          <v-select
            id="clinical"
            class="form-control v-select-custom"
            label="text"
            v-model="clinical_competency_met"
            :reduce="(item) => item.value"
            placeholder="Not assessed yet"
            :options="clinicalOptions"
          >
          </v-select>
        </b-form-group>
      </template>

      <div class="row">
        <b-form-group label="Submitted Date" label-for="submitted_at" label-class="form-label" class="col-md-6">
          <date-picker
            id="submitted_at"
            v-model="submitted_at"
            type="date"
            format="MM/DD/YYYY"
            value-type="YYYY-MM-DD"
            style="width: 100%"
            lang="en"
            placeholder="MM/DD/YYYY"
          >
          </date-picker>
        </b-form-group>
        <b-form-group v-if="$route.params.id" label="Overall Status" label-class="form-label" class="col-md-6">
          <div>
            <b-badge
              variant="none"
              :class="`border border-${statusVariant(overall_status)} text-${statusVariant(overall_status)}`"
              >{{ statusLabel(overall_status) }}</b-badge
            >
            <small class="text-muted ml-2">Derived automatically</small>
          </div>
        </b-form-group>
      </div>

      <b-form-group label="Notes" label-for="notes" label-class="form-label">
        <b-form-textarea id="notes" placeholder="Optional notes" :rows="3" v-model="notes" />
      </b-form-group>

      <!-- Documents -->
      <page-separator title="Evidence Documents" />
      <b-table
        v-if="documents.length"
        :fields="docColumns"
        :items="documents"
        head-variant="light"
        class="table-nowrap mb-3"
        small
        responsive
      >
        <template #cell(kind)="data">{{ docKindLabel(data.item.kind) }}</template>
        <template #cell(file_url)="data">
          <a :href="data.item.file_url" target="_blank" rel="noopener">{{ data.item.filename || 'View' }}</a>
        </template>
        <template #cell(actions)="data">
          <b-btn size="sm" variant="link" class="text-danger p-0" @click="removeDocument(data.index)">
            <md-icon>delete</md-icon>
          </b-btn>
        </template>
      </b-table>

      <div class="row align-items-end">
        <b-form-group label="Document Type" label-for="doc_kind" label-class="form-label" class="col-md-4">
          <v-select
            id="doc_kind"
            class="form-control v-select-custom"
            label="text"
            v-model="newDocKind"
            :reduce="(item) => item.value"
            :options="docKindOptions"
          >
          </v-select>
        </b-form-group>
        <b-form-group label="File" label-for="doc_file" label-class="form-label" class="col-md-6">
          <b-form-file
            id="doc_file"
            placeholder="Select a file"
            :browse-text="$t('generalMsgs.browse')"
            v-model="selectedUploadFile"
            @input="uploadDocument"
            :disabled="isDocUploading"
          />
        </b-form-group>
        <b-form-group class="col-md-2">
          <span v-if="isDocUploading"><i class="fas fa-circle-notch fa-spin"></i> Uploading…</span>
        </b-form-group>
      </div>

      <b-btn variant="primary" :disabled="isLoading || isDocUploading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? $t('update') : $t('add') }}</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
import { debounce, get } from 'lodash';

const PAGE_SIZE = 100;
import { mapActions } from 'vuex';
import PageHeader from '@/components/Ui/PageHeader.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import MdIcon from '@/components/Ui/MdIcon.vue';
import Page from '../../../components/Page.vue';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/locale/en';

const STATUS_META = {
  not_started: { label: 'Not Started', variant: 'secondary' },
  in_progress: { label: 'In Progress', variant: 'warning' },
  passed: { label: 'Passed', variant: 'success' },
  failed: { label: 'Failed', variant: 'danger' },
};

const DOC_KINDS = [
  { value: 'certificate', text: 'Certificate' },
  { value: 'score_report', text: 'Score Report' },
  { value: 'clinical_log', text: 'Clinical Log' },
  { value: 'other', text: 'Other' },
];

export default {
  name: 'StudentAddExamResult',
  components: { PageHeader, PageSeparator, MdIcon, DatePicker },
  extends: Page,

  data() {
    return {
      title: this.$route.params.id ? 'Edit Exam Result' : 'Add Exam Result',

      test_provider: null,
      theory_result: null,
      theory_date: null,
      skill_result: null,
      skill_date: null,
      clinical_competency_met: null,
      submitted_at: null,
      notes: '',
      overall_status: 'not_started',

      allProviders: [],
      existingResults: [], // the student's current live results (for duplicate guard)
      providersHaveMore: false, // total exceeds first page -> use server search
      documents: [], // { id?, kind, file_url, filename }
      deletedDocIds: [],

      newDocKind: 'certificate',
      selectedUploadFile: null,
      isDocUploading: false,

      resultOptions: [
        { value: 'pass', text: 'Pass' },
        { value: 'fail', text: 'Fail' },
      ],
      clinicalOptions: [
        { value: true, text: 'Yes' },
        { value: false, text: 'No' },
      ],
      docKindOptions: DOC_KINDS,

      areProvidersLoading: false,
      isLoading: false,
    };
  },

  computed: {
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Exam Results', to: { name: 'student-exam-results' } },
        {
          text: this.$route.params.id ? 'Edit Exam Result' : 'Add Exam Result',
          active: true,
        },
      ];
    },
    selectedProvider() {
      return this.allProviders.find((p) => p.id === this.test_provider) || null;
    },
    // In create mode, the id of an existing live result for the selected exam (one
    // live result per exam is enforced server-side); null when there is no clash.
    duplicateResultId() {
      if (this.$route.params.id || !this.test_provider) return null;
      const match = this.existingResults.find(
        (r) => get(r, 'test_provider.id', r.test_provider) === this.test_provider
      );
      return match ? match.id : null;
    },
    docColumns() {
      return [
        { key: 'kind', label: 'Type' },
        { key: 'file_url', label: 'File' },
        { key: 'actions', label: '' },
      ];
    },
  },

  methods: {
    ...mapActions('examResult', [
      'getAllExamResults',
      'createExamResult',
      'getExamResult',
      'updateExamResult',
      'createResultDocument',
      'deleteResultDocument',
      'getResultDocUploadPresignedUrl',
    ]),
    ...mapActions('student', ['getMyStudentProfile']),
    ...mapActions('testProvider', ['getAllTestProviders']),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    get,

    statusVariant(status) {
      return get(STATUS_META, `${status}.variant`, 'secondary');
    },
    statusLabel(status) {
      return get(STATUS_META, `${status}.label`, status);
    },
    docKindLabel(kind) {
      return get(
        DOC_KINDS.find((k) => k.value === kind),
        'text',
        kind
      );
    },

    async fetchProviders(search = '') {
      this.areProvidersLoading = true;
      try {
        const response = await this.getAllTestProviders({
          limit: PAGE_SIZE,
          is_active: true,
          ...(search && { search }),
        });
        const results = get(response, 'data.results', get(response, 'data', []));
        const total = get(response, 'data.count', results.length);
        if (!search) this.providersHaveMore = total > PAGE_SIZE;
        this.allProviders = results;
      } catch (e) {
        this.allProviders = [];
      }
      this.areProvidersLoading = false;
    },

    onSearchProviders(search, loading) {
      if (!this.providersHaveMore || !search) return;
      loading(true);
      this.debouncedSearchProviders(this, search, loading);
    },
    debouncedSearchProviders: debounce(async (vm, search, loading) => {
      await vm.fetchProviders(search);
      loading(false);
    }, 400),

    async fetchExistingResults() {
      try {
        const resp = await this.getAllExamResults({ limit: PAGE_SIZE });
        this.existingResults = get(resp, 'data.results', []);
      } catch (e) {
        this.existingResults = [];
      }
    },

    // Preselect the exam whose program matches the student's enrolled program AND
    // that covers the school's state (state is ignored for national providers).
    // School state matches how the backend scopes visible providers; fall back to
    // the student's own state if the school has none.
    async preselectProvider() {
      try {
        const profile = await this.getMyStudentProfile();
        const state = get(profile, 'school.state', get(profile, 'state'));
        const programId = get(profile, 'enrolled_program.id', get(profile, 'enrolled_program'));
        if (!programId) return;
        const resp = await this.getAllTestProviders({ program: programId, is_active: true, limit: PAGE_SIZE });
        const providers = get(resp, 'data.results', get(resp, 'data', []));
        const match = providers.find((p) => p.is_national || (p.states || []).includes(state));
        if (match) {
          if (!this.allProviders.find((p) => p.id === match.id)) this.allProviders.push(match);
          this.test_provider = match.id;
          this.makeToast({ variant: 'success', msg: `Exam preselected: ${match.name}` });
        }
      } catch (e) {
        // Non-fatal: leave the exam unselected for manual choice.
      }
    },

    async uploadDocument() {
      if (!this.selectedUploadFile) return;
      this.isDocUploading = true;
      try {
        const urlResp = await this.getResultDocUploadPresignedUrl({
          file_name: this.selectedUploadFile.name,
          content_type: this.selectedUploadFile.type,
          upload_type: this.newDocKind,
        });
        await this.uploadToPresignedUrl({
          url: urlResp.upload_url,
          file: this.selectedUploadFile,
        });
        this.documents.push({
          kind: this.newDocKind,
          file_url: urlResp.upload_url.split('?')[0],
          filename: this.selectedUploadFile.name,
        });
        this.makeToast({ variant: 'success', msg: 'File uploaded. It will be saved with the result.' });
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Upload failed. Please try again.' });
      }
      this.selectedUploadFile = null;
      this.isDocUploading = false;
    },

    removeDocument(index) {
      const doc = this.documents[index];
      if (doc.id) this.deletedDocIds.push(doc.id);
      this.documents.splice(index, 1);
    },

    async persistDocuments(examResultId) {
      // Create newly added documents.
      const newDocs = this.documents.filter((d) => !d.id);
      await Promise.all(
        newDocs.map((d) =>
          this.createResultDocument({
            exam_result: examResultId,
            kind: d.kind,
            file_url: d.file_url,
            filename: d.filename,
          })
        )
      );
      // Delete removed existing documents.
      await Promise.all(this.deletedDocIds.map((id) => this.deleteResultDocument(id)));
      this.deletedDocIds = [];
    },

    async onSubmit() {
      if (!this.test_provider) {
        this.makeToast({ variant: 'danger', msg: 'Exam is required.' });
        return;
      }
      if (this.duplicateResultId) {
        this.makeToast({
          variant: 'danger',
          msg: 'You already have a result for this exam. Edit the existing result instead.',
        });
        return;
      }
      this.isLoading = true;
      try {
        const provider = this.selectedProvider || {};
        // Student and school are derived server-side from the logged-in user.
        const data = {
          test_provider: this.test_provider,
          theory_result: provider.theory_tested ? this.theory_result : null,
          theory_date: provider.theory_tested ? this.theory_date || null : null,
          skill_result: provider.skill_tested ? this.skill_result : null,
          skill_date: provider.skill_tested ? this.skill_date || null : null,
          clinical_competency_met: provider.clinical_competency_required ? this.clinical_competency_met : null,
          submitted_at: this.submitted_at || null,
          notes: this.notes,
        };

        let examResultId = this.$route.params.id;
        if (examResultId) {
          await this.updateExamResult({ id: examResultId, data });
        } else {
          const created = await this.createExamResult(data);
          examResultId = get(created, 'data.id');
        }

        if (examResultId) await this.persistDocuments(examResultId);

        this.makeToast({
          variant: 'success',
          msg: this.$route.params.id ? 'Exam Result Updated Successfully!' : 'Exam Result Added Successfully!',
        });
        setTimeout(() => this.$router.push({ name: 'student-exam-results' }), 250);
      } catch (err) {
        const msg = get(err, 'response.data.message') || this.$t('generalMsgs.genErrorMsg');
        this.makeToast({ variant: 'danger', msg });
      }
      this.isLoading = false;
    },
  },

  async mounted() {
    await this.fetchProviders();

    if (this.$route.params.id) {
      this.isLoading = true;
      try {
        const resp = (await this.getExamResult(this.$route.params.id)).data;
        this.test_provider = get(resp, 'test_provider.id', resp.test_provider);
        // Ensure the selected provider is present for conditional fields / label
        // (it may fall outside the first page of results).
        if (this.test_provider && resp.test_provider && !this.allProviders.find((p) => p.id === this.test_provider)) {
          this.allProviders.push(resp.test_provider);
        }
        this.theory_result = resp.theory_result;
        this.theory_date = resp.theory_date;
        this.skill_result = resp.skill_result;
        this.skill_date = resp.skill_date;
        this.clinical_competency_met = resp.clinical_competency_met;
        this.submitted_at = resp.submitted_at;
        this.notes = resp.notes || '';
        this.overall_status = resp.overall_status;
        this.documents = (resp.documents || []).map((d) => ({
          id: d.id,
          kind: d.kind,
          file_url: d.file_url,
          filename: d.filename,
        }));
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        this.$router.push({ name: 'student-exam-results' });
      }
      this.isLoading = false;
    } else {
      // New result: load existing results to guard against duplicates, then
      // preselect the exam matching the student's program and state.
      await this.fetchExistingResults();
      await this.preselectProvider();
    }
  },
};
</script>
